import { useMemo, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { PackageOpen, Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { productsApi, profileClientApi } from "@/lib/api";
import {
  allPickerKeys,
  filterProductGroups,
  groupProductRows,
  seriesOfGroup,
  type ProductPickerRow,
} from "@/lib/productPickerGroups";
import { cn } from "@/lib/utils";

interface IntegratedOrderProductPickerProps {
  clientCode: string;
  cycleYear: number;
  /** Produto escolhido (product_id); nulo = nenhum. */
  value: number | null;
  onChange: (productId: number) => void;
  disabled?: boolean;
}

interface PickerRow extends ProductPickerRow {
  contractualQuantity: number;
}

/**
 * Seleção do produto de um registro do ERP, no mesmo padrão do seletor de itens do cupom: produtos ativos da escola
 * no ciclo (rotas já existentes de perfil e produtos), agrupados por segmento e série, com busca por descrição,
 * código ou SKU. Escolha única.
 */
export function IntegratedOrderProductPicker({
  clientCode,
  cycleYear,
  value,
  onChange,
  disabled,
}: IntegratedOrderProductPickerProps) {
  const profileQuery = useQuery({
    queryKey: ["integrated-orders", "product-picker", "profile", clientCode, cycleYear],
    queryFn: () => profileClientApi.getByClientCode(clientCode, cycleYear),
    staleTime: 60_000,
  });
  const productsQuery = useQuery({
    queryKey: ["integrated-orders", "product-picker", "products", cycleYear],
    queryFn: () => productsApi.getAll(cycleYear),
    staleTime: 60_000,
  });

  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState<string[] | null>(null);

  const rows = useMemo<PickerRow[]>(() => {
    const products = productsQuery.data?.data ?? [];
    const byProduct = new Map<number, PickerRow>();
    for (const entry of (profileQuery.data?.data ?? []) as any[]) {
      const productId = Number(entry.productId);
      const product = products.find((candidate) => candidate.id === productId);
      if (!productId || !product) continue;
      const contractual = Number(entry.contractualQuantity) || 0;
      const existing = byProduct.get(productId);
      if (existing) {
        existing.contractualQuantity += contractual;
        continue;
      }
      byProduct.set(productId, {
        productId,
        productSeries: product.serie ?? null,
        // A API de produtos devolve a descrição em camelCase (descricaoProduto), como o pedido já lê.
        description: (product as any).descricaoProduto ?? product.descricao_produto ?? "",
        code: product.codigoProduto || product.codigo_produto || "",
        sku: product.sku,
        usage: (product.utilizacao ?? "").trim(),
        contractualQuantity: contractual,
      });
    }
    return Array.from(byProduct.values());
  }, [profileQuery.data, productsQuery.data]);

  const groups = useMemo(() => groupProductRows(rows), [rows]);
  const visibleGroups = filterProductGroups(groups, query);

  // Abertos ao montar: os segmentos (todos até 3, ou o primeiro mais o que tem o produto escolhido) e a série dele.
  const expanded = openGroups ?? [
    ...groups
      .filter((group, index) => groups.length <= 3 || index === 0 || group.items.some((row) => row.productId === value))
      .map((group) => group.key),
    ...groups.flatMap((group) =>
      seriesOfGroup(group).filter((block) => block.items.some((row) => row.productId === value)).map((block) => block.key)),
  ];

  const handleSearchChange = (text: string) => {
    setQuery(text);
    if (text.trim()) setOpenGroups(allPickerKeys(filterProductGroups(groups, text)));
  };

  // Enter na busca não pode enviar o formulário.
  const preventSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") event.preventDefault();
  };

  if (profileQuery.isLoading || productsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (profileQuery.isError || productsQuery.isError) {
    return <p className="text-sm text-destructive">Não foi possível carregar os produtos da escola neste ciclo.</p>;
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-[10px] border border-dashed px-6 py-8 text-center">
        <PackageOpen className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Esta escola não possui produtos ativos no ciclo {cycleYear}.</p>
      </div>
    );
  }

  const segmentKeys = new Set(groups.map((group) => group.key));
  // Cada nível troca só as suas chaves e preserva as do outro.
  const updateLevel = (levelKeys: Set<string>) => (next: string[]) =>
    setOpenGroups([...expanded.filter((key) => !levelKeys.has(key)), ...next]);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={query}
          onChange={(event) => handleSearchChange(event.target.value)}
          onKeyDown={preventSubmit}
          placeholder="Buscar por descrição, código ou SKU"
          aria-label="Buscar produto por descrição, código ou SKU"
          autoComplete="off"
          className="h-10 pl-9 text-[14.5px]"
        />
      </div>

      {visibleGroups.length === 0 ? (
        <p className="rounded-[10px] border border-dashed px-6 py-6 text-center text-sm text-muted-foreground">
          Nenhum produto encontrado para "{query.trim()}".
        </p>
      ) : (
        <RadioGroup
          value={value != null ? String(value) : ""}
          onValueChange={(next) => onChange(Number(next))}
          disabled={disabled}
          className="gap-0"
        >
          <Accordion
            type="multiple"
            value={expanded.filter((key) => segmentKeys.has(key))}
            onValueChange={updateLevel(segmentKeys)}
            className="flex flex-col gap-2"
          >
            {visibleGroups.map((group) => {
              const hasSelected = group.items.some((row) => row.productId === value);
              const blocks = seriesOfGroup(group);
              const blockKeys = new Set(blocks.map((block) => block.key));
              return (
                <AccordionItem key={group.key} value={group.key} className="rounded-[10px] border bg-background">
                  <AccordionTrigger className="gap-3 px-4 py-3 text-left hover:no-underline">
                    <span className="min-w-0 flex-1 text-[15px] font-bold">{group.key}</span>
                    <span
                      className={
                        hasSelected
                          ? "shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
                          : "shrink-0 text-xs text-muted-foreground"
                      }
                    >
                      {hasSelected
                        ? "selecionado"
                        : `${group.items.length} ${group.items.length === 1 ? "produto" : "produtos"}`}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <Accordion
                      type="multiple"
                      value={expanded.filter((key) => blockKeys.has(key))}
                      onValueChange={updateLevel(blockKeys)}
                      className="flex flex-col gap-2"
                    >
                      {blocks.map((block) => {
                        const blockSelected = block.items.some((row) => row.productId === value);
                        return (
                          <AccordionItem key={block.key} value={block.key} className="rounded-lg border bg-muted/20">
                            <AccordionTrigger className="gap-3 px-3 py-2.5 text-left hover:no-underline">
                              <span className="min-w-0 flex-1 text-[14px] font-semibold">{block.name}</span>
                              <span
                                className={
                                  blockSelected
                                    ? "shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary"
                                    : "shrink-0 text-xs text-muted-foreground"
                                }
                              >
                                {blockSelected
                                  ? "selecionado"
                                  : `${block.items.length} ${block.items.length === 1 ? "produto" : "produtos"}`}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-2.5 px-2.5 pb-2.5">
                              {block.items.map((row) => (
                                <ProductCard key={row.productId} row={row} selected={row.productId === value} disabled={disabled} />
                              ))}
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </RadioGroup>
      )}
    </div>
  );
}

function ProductCard({ row, selected, disabled }: { row: PickerRow; selected: boolean; disabled?: boolean }) {
  const controlId = `integrated-order-product-${row.productId}`;
  return (
    <label
      htmlFor={controlId}
      className={cn(
        "flex items-center gap-3.5 rounded-[10px] border py-3.5 pl-4 pr-4 transition-colors",
        selected ? "border-2 border-primary bg-primary/5 py-[13px] pl-[15px]" : "hover:bg-muted/40",
        disabled ? "cursor-default opacity-80" : "cursor-pointer",
      )}
    >
      <RadioGroupItem id={controlId} value={String(row.productId)} className="h-5 w-5 shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[15px] font-semibold leading-snug">{row.description || "Produto sem descrição"}</span>
          <span className="text-[13px] text-muted-foreground">Contratual: {row.contractualQuantity}</span>
        </span>
        <span className="text-xs text-muted-foreground">
          {row.code || "—"} · SKU {row.sku || "—"} · {row.usage || "—"}
        </span>
      </span>
    </label>
  );
}
